/**
 * @fileoverview A reusable multi-select component with tooltip functionality
 * @requires react
 * @requires antd
 */

import React from "react"
import { Select, Tooltip } from "antd"
import type { SelectOption } from "types/ui.types"

const { Option } = Select

/**
 * Props for the CustomSelect component
 */
export interface CustomSelectProps {
  /** Placeholder text for the select */
  placeholder: string
  /** Currently selected values */
  value: string[]
  /** Change handler function */
  onChange: (values: string[]) => void
  /** Available options */
  options: SelectOption[]
  /** Optional width override (defaults to 200px) */
  width?: number
  /** Optional tooltip placement */
  tooltipPlacement?:
    | "bottomLeft"
    | "bottomRight"
    | "topLeft"
    | "topRight"
    | "bottom"
    | "top"
}

/**
 * Custom select component with tooltip showing selected items
 * Features:
 * - Multi-select with compact display (shows count instead of all selected items)
 * - Tooltip shows full list of selected items on hover
 * - Configurable width and tooltip placement
 */
export const CustomSelect: React.FC<CustomSelectProps> = ({
  placeholder,
  value,
  onChange,
  options,
  width = 200,
  tooltipPlacement = "bottomLeft",
}) => {
  const selected = options.filter((option: SelectOption) =>
    value.includes(option.value)
  )
  const tooltipTitle = selected
    .map((item: SelectOption) => item.label)
    .join(", ")

  return (
    <Tooltip title={tooltipTitle} placement={tooltipPlacement}>
      <Select
        mode="multiple"
        style={{ width }}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        maxTagCount={0}
        maxTagPlaceholder={(omittedValues) =>
          `${omittedValues.length} selected`
        }
      >
        {options.map((option: SelectOption) => (
          <Option key={option.value} value={option.value}>
            {option.label}
          </Option>
        ))}
      </Select>
    </Tooltip>
  )
}

export default CustomSelect
